import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { InputComponent } from "@components/input/input.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { Anskaffningsformer, Avverkningsgrupper } from "~app/common/models/constants";
import { FeatureService } from "~app/common/services/feature.service";
import { IProduktionsmalsOmrade } from "~app/features/planera/model/IProduktionsmalsOmrade";
import { ArbetsorderSearchBase } from "../../models/arbetsorder-search-base";

@Component({
    selector: "rask-arbetsorder-search-header-filter",
    templateUrl: "./arbetsorder-search-header-filter.component.html",
    styleUrls: ["./arbetsorder-search-header-filter.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TreeSelectorComponent,
        InputComponent,
        FormsModule
    ]
})
export class ArbetsorderSearchHeaderFilterComponent extends ArbetsorderSearchBase implements OnInit {
    affarsformItems: SelectionItem[] = [];
    anskaffningsformItems: SelectionItem[] = [];
    avverkningstypItems: SelectionItem[] = [];
    geografiItems: SelectionItem[] = [];

    private readonly featureService = inject(FeatureService);

    applyFilter() {
        this.anskaffningsformItems.forEach(form => {
            form.selected = (this.searchFilter.anskaffningsform.valda.indexOf(form.value) > -1);
            if (Anskaffningsformer.isVirkesKop(form.value)) {
                this.affarsformItems.forEach(affarsform => {
                    affarsform.selected = (this.searchFilter.virkeskop.affarsformer.indexOf(affarsform.value) > -1);
                });
            }
        });

        this.geografiItems.forEach(mo => {
            mo.selected = (this.searchFilter.omraden.find(omrade => omrade.id === mo.id && omrade.typ === "MO") !== undefined);
            mo.children.forEach(ro => {
                ro.selected = (this.searchFilter.omraden.find(omrade => omrade.id === ro.id && omrade.typ === "RO") !== undefined);
                ro.children.forEach(po => {
                    // eslint-disable-next-line sonarjs/no-nested-functions
                    po.selected = (this.searchFilter.omraden.find(omrade => omrade.id === po.id && omrade.typ === "PO") !== undefined);
                });
                ro.partialSelected = (!ro.selected && ro.children.some(po => po.selected));
            });
            mo.partialSelected = (!mo.selected && mo.children.some(ro => ro.selected || ro.partialSelected));
        });
        this.avverkningstypItems.forEach(item => {
            item.selected = (this.searchFilter.avverkningsgrupper.includes(item.text));
        });
    }

    canUseAffarsform(): boolean {
        return this.searchFilter.anskaffningsform.valda.some(x => Anskaffningsformer.isVirkesKop(x));
    }

    forceUpdateFilterChange(): void {
        this.applyFilter();
    }

    ngOnInit() {
        this.setupSelectionItems();
        this.applyFilter();
    }

    setAnskaffningsformFromSelector() {
        this.searchFilter.anskaffningsform.valda = this.anskaffningsformItems
            .filter(form => form.selected || form.partialSelected)
            .map(form => form.value);

        if(!this.canUseAffarsform()) {
            this.searchFilter.virkeskop.affarsformer = [];
            this.affarsformItems.forEach(x => x.selected = false);
        } else {
            this.searchFilter.virkeskop.affarsformer = this.affarsformItems.filter(item => item.selected).map(item => item.value);
        }
    }

    setAvverkningstypFromSelector() {
        this.avverkningstypItems.forEach(item => {
            if (item.selected && !this.searchFilter.avverkningsgrupper.includes(item.text)) {
                this.searchFilter.avverkningsgrupper.push(item.text);
            }
            if (!item.selected && this.searchFilter.avverkningsgrupper.includes(item.text)) {
                this.searchFilter.avverkningsgrupper.splice(this.searchFilter.avverkningsgrupper.indexOf(item.text), 1);
            }
        });
    }

    setGeografiItemsFromSelector() {
        const tempItems: IProduktionsmalsOmrade[] = [];
        this.geografiItems.forEach(mo => {
            if (mo.selected) {
                tempItems.push({ id: mo.id,
                    typ: "MO" });
            }
            mo.children.forEach(ro => {
                if (ro.selected) {
                    tempItems.push({ id: ro.id,
                        typ: "RO" });
                }
                ro.children.forEach(po => {
                    if (po.selected) {
                        tempItems.push({ id: po.id,
                            typ: "PO" });
                    }
                });
            });
        });
        this.searchFilter.omraden = tempItems;
    }

    setupSelectionItems() {
        this.avverkningstypItems = this.basdata.avverkningsgrupper
            .filter(typ => typ !== Avverkningsgrupper.Grotskotning)
            .map(typ => ({ text: typ,
                value: typ }));

        const tempAnskaffningsFormer = [];
        tempAnskaffningsFormer.push({ text: Anskaffningsformer.Egenskog,
            value: Anskaffningsformer.Egenskog });

        tempAnskaffningsFormer.push({
            text: Anskaffningsformer.Virkeskop,
            value: Anskaffningsformer.Virkeskop
        });

        tempAnskaffningsFormer.push({ text: Anskaffningsformer.CentralaVirkeskop,
            value: Anskaffningsformer.CentralaVirkeskop });

        tempAnskaffningsFormer.push({ text: Anskaffningsformer.CentralaKop,
            value: Anskaffningsformer.CentralaKop });
        tempAnskaffningsFormer.push({ text: Anskaffningsformer.EgenTerminal,
            value: Anskaffningsformer.EgenTerminal });
        tempAnskaffningsFormer.push({ text: Anskaffningsformer.Import,
            value: Anskaffningsformer.Import });

        this.anskaffningsformItems = tempAnskaffningsFormer;

        this.affarsformItems = this.basdata.affarsformer.map(form => ({ text: `${form.key} ${form.value}`,
            value: form.value }));

        this.geografiItems = this.basdata.marknadsomraden.map(marknadsOmrade => ({
            id: marknadsOmrade.id,
            text: marknadsOmrade.namn,
            children: marknadsOmrade.children.map(resultatOmrade => ({
                id: resultatOmrade.id,
                text: resultatOmrade.namn,
                children: resultatOmrade.children.map(produktionsOmrade => ({
                    id: produktionsOmrade.id,
                    text: produktionsOmrade.namn
                }))
            }))
        } as SelectionItem));
    }
}
